/**
 * Appvia Backstage Styles
 *
 * This package provides customization modules for Backstage that apply Appvia branding,
 * theme, and custom components.
 *
 * @packageDocumentation
 */

// Import CSS (this ensures it's bundled with the package)
import './styles/fonts.css';
import './styles/unified-theme-variables.css';
import './styles/tailwind.output.css';

// Export all modules individually
export {
  appviaThemeModule,
  appviaLayoutModule,
  appviaHomeModule,
  appviaCatalogModule,
  appviaScaffolderModule,
  appviaTechDocsModule,
  appviaSearchModule,
  appviaApiDocsModule,
  appviaUserSettingsModule,
  appviaNavContentModule,
  appviaCatalogGraphModule,
} from './modules';

// Import for convenience array
import {
  appviaThemeModule,
  appviaLayoutModule,
  appviaHomeModule,
  appviaCatalogModule,
  appviaScaffolderModule,
  appviaTechDocsModule,
  appviaSearchModule,
  appviaApiDocsModule,
  appviaUserSettingsModule,
  appviaNavContentModule,
  appviaCatalogGraphModule,
} from './modules';

/**
 * All Appvia modules bundled together.
 *
 * Default export (recommended - follows Backstage new frontend system pattern):
 * ```typescript
 * import appviaStyles from '@appvia/backstage-styles';
 *
 * const app = createApp({
 *   features: [...appviaStyles],
 * });
 * ```
 *
 * Or use named export for backward compatibility:
 * ```typescript
 * import { appviaModules } from '@appvia/backstage-styles';
 *
 * const app = createApp({
 *   features: [...appviaModules],
 * });
 * ```
 *
 * @public
 */
const appviaStyles = [
  appviaThemeModule,
  appviaLayoutModule,
  appviaHomeModule,
  appviaCatalogModule,
  appviaScaffolderModule,
  appviaTechDocsModule,
  appviaSearchModule,
  appviaApiDocsModule,
  appviaUserSettingsModule,
  appviaNavContentModule,
  appviaCatalogGraphModule,
];

// Default export (new frontend system pattern)
export default appviaStyles;

// Named export for backward compatibility
export const appviaModules = appviaStyles;

// Export theme for external use
export { appviaTheme } from './styles/theme';
export {
  appviaBrandColors,
  lightTheme,
  darkTheme,
  typography,
  spacing,
  shadows,
  chartColors,
  getThemeConfig,
} from './styles/unified-theme-config';

// Export useful components
export { LogoFull, LogoIcon } from './components';

// Export hooks
export { useNavigationConfig, useCatalogSchema } from './hooks';
export { useHomeConfig } from './extensions/home';

// Export types
export type {
  NavigationConfig,
  NavigationGroup,
  NavigationItem,
} from './types/navigation';
export type {
  HomeConfig,
  HomeLayoutConfig,
  HomeBannerConfig,
  HomeSection,
  HomeSectionItem,
  HomeGridConfig,
  PluginWidget,
  HomeSettings,
  HomeWidgetMetadata,
} from './extensions/home';

// Export utilities
export { AppIcons, NavigationDefaults, createDebugLogger } from './utils';
export { DEFAULT_HOME_CONFIG, generateDefaultSections } from './extensions/home';

// Export blueprints for third-party plugin integration
export { HomeWidgetBlueprint, homeRouteRef } from './extensions/home';

// Export widget data refs for third-party plugins
export {
  homeWidgetTitleDataRef,
  homeWidgetPriorityDataRef,
  homeWidgetColumnDataRef,
} from './extensions/home';